import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import landingPageCSS from './LandingPage.module.css'

import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import SectionNewListings from '../PageBuilder/SectionBuilder/SectionNewListings'; // Import the new component
import SectionEditsListings from '../PageBuilder/SectionBuilder/SectionEditsListings'; // Import the edits component
import SectionAppPromotion from '../PageBuilder/SectionBuilder/SectionAppPromotion'; // Import the app promotion component
import SectionInstagramPosts from '../PageBuilder/SectionBuilder/SectionInstagramPosts'; // Import the app promotion component

import { validProps } from '../../containers/PageBuilder/Field';

import SectionBuilder from '../PageBuilder/SectionBuilder';  // Adjusted import path
import StaticPage from '../PageBuilder/StaticPage';  // Adjusted import path
import css from '../PageBuilder/PageBuilder.module.css';  // Adjusted import path

const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  const title = validProps(pageTitle, fieldOptions)?.content;
  const description = validProps(pageDescription, fieldOptions)?.content;
  const openGraph = validProps(socialSharing, fieldOptions);
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const rootColor = "white";

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    ...pageProps
  } = props;

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  const { sections = [], meta = {} } = pageAssetsData || {};
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = `
    topbar
    main
    footer
  `;
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} />
              </Topbar>
              <Main as="main" className={css.main}>
                <section className={landingPageCSS.heroSection}>
                  <video
                    className={landingPageCSS.backgroundVideo}
                    src="https://cdn.prod.website-files.com/641234196da95de0da5f4ec7/6436da2347c58f6cd5a5548b_YOUNG%20LOVED%2060s%20EDIT%20LOGO%20NO%20MUSIC-transcode.mp4"
                    type="video/mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                  <div className={landingPageCSS.content}>
                    <h1 className={landingPageCSS.title}>
                      <FormattedMessage id="LandingPage.schemaTitle" />
                    </h1>
                    <p className={landingPageCSS.subtitle}>
                      <FormattedMessage id="LandingPage.schemaDescription" />
                    </p>
                    <div className={landingPageCSS.buttons}>
                      <a
                        href="https://apps.apple.com/nl/app/young-loved/id6464721037?l=en-GB"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={landingPageCSS.buttonLink}
                      >
                        <button className={landingPageCSS.buttonPrimary}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className={landingPageCSS.iconApple}>
                            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" className={css.iconPath} />
                          </svg>
                          <FormattedMessage id="LandingPage.appleButton" />
                        </button>
                      </a>

                      <a
                        href="https://play.google.com/store/apps/details?id=com.youngloved"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={landingPageCSS.buttonLink}
                      >
                        <button className={landingPageCSS.buttonSecondary}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" className={landingPageCSS.iconAndroid}>
                            <path d="M420.6 301.9a24 24 0 1 1 24-24 24 24 0 0 1 -24 24m-265.1 0a24 24 0 1 1 24-24 24 24 0 0 1 -24 24m273.7-144.5 47.9-83a10 10 0 1 0 -17.3-10h0l-48.5 84.1a301.3 301.3 0 0 0 -246.6 0L116.2 64.5a10 10 0 1 0 -17.3 10h0l47.9 83C64.5 202.2 8.2 285.6 0 384H576c-8.2-98.5-64.5-181.8-146.9-226.6" className={css.iconPath} />
                          </svg>
                          <FormattedMessage id="LandingPage.androidButton" />
                        </button>
                      </a>
                    </div>
                  </div>
                </section>
                <div className={landingPageCSS.mainContent}>
                <section className={landingPageCSS.uspSection}>
                  <div className={landingPageCSS.roundedContainer}>
                    <div className={landingPageCSS.item}>
                      <img src="/static/icons/premium-icon.svg" alt="Premium Icon" className={landingPageCSS.icon} />
                      <p className={landingPageCSS.uspText}>
                        <FormattedMessage id="LandingPage.firstUSPText" />
                      </p>
                    </div>
                    <div className={landingPageCSS.item}>
                      <img src="/static/icons/no-fee-icon.svg" alt="No Fee Icon" className={landingPageCSS.icon} />
                      <p className={landingPageCSS.uspText}>
                        <FormattedMessage id="LandingPage.secondUSPText" />
                      </p>
                    </div>
                    <div className={landingPageCSS.item}>
                      <img src="/static/icons/employee-icon.svg" alt="Employee Icon" className={landingPageCSS.icon} />
                      <p className={landingPageCSS.uspText}>
                        <FormattedMessage id="LandingPage.thirdUSPText" />
                      </p>
                    </div>
                    <div className={landingPageCSS.item}>
                      <img src="/static/icons/delivery-service-icon.svg" alt="Delivery Service Icon" className={landingPageCSS.icon} />
                      <p className={landingPageCSS.uspText}>
                        <FormattedMessage id="LandingPage.fourthUSPText" />
                      </p>
                    </div>
                  </div>
                </section>
                {/* New Listings Section */}
                <SectionNewListings />
                {/* New Image Section */}
                <section className={landingPageCSS.quoteNewImageSection}>
                  <div className={landingPageCSS.quoteTextContainer}>
                    <div
                      className={landingPageCSS.quoteBackgroundImage}
                      style={{ backgroundImage: `url('/static/images/quoteImage.jpg')` }}
                    ></div>
                    <div className={landingPageCSS.quoteTextOverlay}>
                      <h1 className={landingPageCSS.quoteLargeText}>
                        <FormattedMessage id="LandingPage.quoteSectionTitle" defaultMessage="Your Large Text Here" />
                      </h1>
                    </div>
                  </div>
                </section>
                {/* New Listings Section */}
                <SectionEditsListings />
                {/* App Promotion Section */}
                <SectionAppPromotion />
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <SectionBuilder rootColor="white" sections={sections} options={options} />
                )}
                {/* Instagram Section */}
                <SectionInstagramPosts />
                </div>
              </Main>
              <Footer>
                <FooterContainer rootColor={rootColor}/>
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  return (
    <div>
      <PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
      />
    </div>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;